import { FEATURE_FLAGS } from '@aibees/elephant/constants';
import { FeatureFlags, SearchType } from '@aibees/elephant/types';
import { ExportTitles } from '@aibees/elephant/types/bootstrap';
import { IntegrationConfig } from '@aibees/elephant/types/integration';
import { IconType } from '@aibees/shared/components';
import { isFeatureEnabled } from '@aibees/shared/feature-flags';
import {
  CategoryKey,
  CategoryType,
  OptionType,
  TemplateCategory,
  TemplateOption,
} from '@aibees/shared/features';

// Mapping from option keys to category information
const categoryMapping: Record<
  string,
  {
    key: CategoryKey;
    name: string;
    type: CategoryType;
    hasInfoBadge?: boolean;
    isSingleEntry?: boolean;
    disabled?: boolean;
    hidden?: boolean;
  }
> = {
  Basic: {
    key: CategoryKey.basic,
    name: 'People Basic',
    type: CategoryType.PEOPLE,
    isSingleEntry: true,
  },
  Company: {
    key: CategoryKey.company,
    name: 'Company Basic',
    type: CategoryType.COMPANY,
    isSingleEntry: true,
  },
  // Email: {
  //   key: CategoryKey.email,
  //   name: 'Email Basic',
  //   type: CategoryType.EMAIL,
  //   isSingleEntry: true,
  // },
  CompanyLocation: {
    key: CategoryKey.companyLocations,
    name: 'Company Location',
    type: CategoryType.COMPANY,
    hasInfoBadge: true,
  },
  Experience: {
    key: CategoryKey.experiences,
    name: 'Experience',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
  },
  Education: {
    key: CategoryKey.educations,
    name: 'Education',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
  },
  Award: {
    key: CategoryKey.awards,
    name: 'Awards',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
  },
  Course: {
    key: CategoryKey.courses,
    name: 'Courses',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
  },
  Project: {
    key: CategoryKey.projects,
    name: 'Projects',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
  },
  Language: {
    key: CategoryKey.languages,
    name: 'Languages',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
  },
  Certification: {
    key: CategoryKey.certifications,
    name: 'Certification',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
  },
  VolunteerExperience: {
    key: CategoryKey.volunteerExperiences,
    name: 'Volunteering',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
  },
  Publication: {
    key: CategoryKey.publications,
    name: 'Publications',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
  },
  Patent: {
    key: CategoryKey.patents,
    name: 'Patent',
    type: CategoryType.PEOPLE,
    hasInfoBadge: true,
    disabled: true,
  },
};

export const convertExportOptionsToArrayWithTypes = (
  titles: ExportTitles
): TemplateOption[] => {
  const result: TemplateOption[] = [];

  // Sort categories by order
  const sortedCategories = Object.entries(titles).sort(
    ([, a], [, b]) => a.order - b.order
  );

  sortedCategories.forEach(([category, categoryData]) => {
    // Sort columns by order
    const sortedColumns = Object.entries(categoryData.columns).sort(
      ([, a], [, b]) => a.order - b.order
    );

    sortedColumns.forEach(([fieldKey, fieldData]) => {
      result.push({
        name: fieldData.label,
        selected: true,
        type: `${category}.${fieldKey}` as OptionType,
      });
    });
  });

  return result;
};

export const icon: Record<string, IconType> = {
  title: 'title',
  hashtag: 'hashtag-solid',
  calendar: 'calendar-today-outline',
  url: 'insert-link',
  checkbox: 'check',
  email: 'alternate-email',
  company: 'business',
};

export const convertOptionsToTemplateCategories = (
  optionsData: ExportTitles,
  integrationConfig?: IntegrationConfig,
  searchType?: SearchType
): TemplateCategory[] => {
  const result: TemplateCategory[] = [];
  const isCompany = searchType === SearchType.Company;

  // Helper function to get appropriate icon based on field type/name
  const getIconForField = (fieldKey: string, fieldName: string): IconType => {
    const lowerFieldName = fieldName.toLowerCase();
    const lowerFieldKey = fieldKey.toLowerCase();

    if (
      lowerFieldName.includes('url') ||
      lowerFieldName.includes('website') ||
      lowerFieldName.includes('linkedin') ||
      lowerFieldName.includes('facebook') ||
      lowerFieldName.includes('twitter') ||
      lowerFieldName.includes('instagram') ||
      lowerFieldKey.includes('search_input') ||
      lowerFieldKey.includes('mx_record') ||
      lowerFieldKey.includes('logo')
    )
      return icon.url;
    if (lowerFieldName.includes('email')) return icon.email;
    if (
      lowerFieldName.includes('phone') ||
      lowerFieldName.includes('number') ||
      lowerFieldKey.includes('org_id') ||
      lowerFieldName.includes('grade') ||
      lowerFieldKey.includes('id') ||
      lowerFieldKey.includes('company_id') ||
      lowerFieldKey.includes('last_update')
    )
      return icon.hashtag;
    if (lowerFieldKey.includes('country') || lowerFieldName.includes('org'))
      return icon.title;

    if (lowerFieldName.includes('date') || lowerFieldKey.includes('date'))
      return icon.calendar;
    // if (lowerFieldName.includes('company')) return icon.company;

    // if (lowerFieldName.includes('primary')) return icon.checkbox;

    return icon.title; // default icon
  };

  // Helper function to determine if field should be selected by default
  const isFieldSelectedByDefault = (
    fieldKey: string,
    fieldName: string,
    categoryName?: string
  ): boolean => {
    const lowerFieldName = fieldName.toLowerCase();
    const lowerFieldKey = fieldKey.toLowerCase();
    const lowerCategoryName = categoryName?.toLowerCase() || '';

    if (lowerCategoryName.includes('people basic')) {
      if (lowerFieldName.includes('title')) return true;
      if (lowerFieldName.includes('location')) return true;
      if (lowerFieldName.includes('seniority')) return true;
      if (lowerFieldName.includes('department')) return true;
      if (lowerFieldName.includes('first name')) return true;
      if (lowerFieldName.includes('last name')) return true;
      if (lowerFieldName.includes('full name')) return true;
      if (lowerFieldName.includes('linkedin')) return true;
      if (lowerFieldName.includes('mobile phone')) return true;
      // if (lowerFieldName.includes('headline')) return true;
      // if (lowerFieldName.includes('summary')) return true;
      if (lowerFieldName.includes('country')) return true;
      if (lowerFieldName.includes('state')) return true;
      if (lowerFieldName.includes('city')) return true;
      if (lowerFieldName.includes('org')) return true;
      // if (lowerFieldName.includes('influencer')) return true;
      // if (lowerFieldName.includes('premium')) return true;
      // if (lowerFieldName.includes('open to work')) return true;
      // if (lowerFieldName.includes('creator')) return true;
      if (lowerFieldName.includes('mx records')) return true;
      if (lowerFieldKey.includes('smtp_provider')) return true;
      if (lowerFieldName.includes('email business')) return true;
      if (lowerFieldName.includes('business status')) return true;
      if (lowerFieldName.includes('domain settings')) return true;
      if (lowerFieldName.includes('ai ark contact id')) return true;
      if (lowerFieldKey.includes('last_update')) return true;
    }

    // if (lowerCategoryName.includes('experience')) {
    //   if (lowerFieldName.includes('url')) return true;
    //   if (lowerFieldName.includes('title')) return true;
    //   if (lowerFieldName.includes('company url')) return true;
    //   if (lowerFieldName.includes('company')) return true;
    //   if (lowerFieldName.includes('is current')) return true;
    //   if (lowerFieldName.includes('start date')) return true;
    //   if (lowerFieldName.includes('end date')) return true;
    //   if (lowerFieldName.includes('locality')) return true;
    // }

    // if (lowerCategoryName.includes('education')) {
    //   if (lowerFieldName.includes('school name')) return true;
    //   if (lowerFieldKey.includes('field')) return true;
    // }

    // if (lowerCategoryName.includes('award')) {
    //   if (lowerFieldName.includes('title')) return true;
    //   if (lowerFieldName.includes('company name')) return true;
    // }

    // if (lowerCategoryName.includes('course')) {
    //   if (lowerFieldName.includes('title')) return true;
    // }

    // if (lowerCategoryName.includes('project')) {
    //   if (lowerFieldName.includes('title')) return true;
    // }

    // if (lowerCategoryName.includes('languages')) {
    //   if (lowerFieldName.includes('language')) return true;
    // }

    // if (lowerCategoryName.includes('certification')) {
    //   if (lowerFieldName.includes('title')) return true;
    // }

    // if (lowerCategoryName.includes('volunteering')) {
    //   if (lowerFieldName.includes('summary')) return true;

    //   if (lowerFieldName.includes('company name')) return true;
    // }

    // if (lowerCategoryName.includes('publications')) {
    //   if (lowerFieldName.includes('title')) return true;
    // }

    // if (lowerCategoryName.includes('patent')) {
    //   if (lowerFieldName.includes('title')) return true;
    // }
    // if (lowerCategoryName.includes('email')) {
    //   if (lowerFieldName.includes('email')) return true;
    //   if (lowerFieldName.includes('domain')) return true;
    //   if (lowerFieldName.includes('domain settings')) return true;
    //   if (lowerFieldName.includes('mx records')) return true;
    //   if (lowerFieldName.includes('email business')) return true;
    //   if (lowerFieldName.includes('business status')) return true;
    //   if (lowerFieldName.includes('email verifier')) return true;
    //   if (lowerFieldName.includes('email finder')) return true;
    //   if (lowerFieldName.includes('email verifier')) return true;
    //   if (lowerFieldName.includes('name')) return true;
    // }

    if (lowerCategoryName.includes('company basic')) {
      if (lowerFieldName.includes('name')) return true;
      if (lowerFieldName.includes('headcount')) return true;
      if (lowerFieldName.includes('primary company phone')) return true;
      if (lowerFieldName.includes('size')) return true;
      if (lowerFieldName.includes('industry')) return true;
      if (lowerFieldName.includes('product and services')) return true;
      if (lowerFieldName.includes('description')) return true;
      if (lowerFieldName.includes('seo description')) return true;
      if (lowerFieldName.includes('website')) return true;
      if (lowerFieldName.includes('linkedin')) return true;
      if (lowerFieldName.includes('twitter') || lowerFieldName.includes('x'))
        return true;
      if (lowerFieldName.includes('facebook')) return true;
      if (lowerFieldName.includes('instagram')) return true;
      if (lowerFieldName.includes('type')) return true;
      if (lowerFieldName.includes('number of location')) return true;
      if (lowerFieldName.includes('location')) return true;
      if (lowerFieldName.includes('primary phone')) return true;
      if (lowerFieldName.includes('founding year')) return true;
      if (lowerFieldName.includes('annual revenue')) return true;
      if (lowerFieldName.includes('total funding')) return true;
      if (lowerFieldName.includes('last funding type')) return true;
      if (lowerFieldName.includes('last funding amount')) return true;
      if (lowerFieldName.includes('last funding date')) return true;
      if (lowerFieldKey.includes('domain')) return isCompany ? true : false;
      if (lowerFieldKey.includes('location')) return isCompany ? true : false;
      if (lowerFieldKey.includes('technologies')) return true;
      if (lowerFieldKey.includes('city')) return true;
      if (lowerFieldKey.includes('state')) return true;
      if (lowerFieldKey.includes('country')) return true;
      if (lowerFieldKey.includes('id')) return true;
      if (lowerFieldKey.includes('primary_email')) return true;
      if (lowerFieldKey.includes('last_update')) return true;
    }

    // if (lowerCategoryName.includes('company location')) {
    //   if (lowerFieldName.includes('primary')) return true;
    //   if (lowerFieldName.includes('address')) return true;
    // }

    return false;
  };

  const isIntegrationFieldSelectedByDefault = (
    fieldKey: string,
    fieldName: string,
    categoryName?: string
  ): boolean => {
    const lowerFieldName = fieldName.toLowerCase();
    const lowerFieldKey = fieldKey.toLowerCase();
    const lowerCategoryName = categoryName?.toLowerCase() || '';

    if (lowerCategoryName.includes('people basic')) {
      if (lowerFieldName.includes('first name')) return true;
      if (lowerFieldName.includes('last name')) return true;
      if (lowerFieldName.includes('mobile phone')) return true;
      if (lowerFieldName.includes('blog/website')) return true;
      if (lowerFieldName.includes('location')) return true;
      if (lowerFieldName.includes('linkedin')) return true;
      if (lowerFieldName.includes('email business')) return true;
    }

    if (lowerCategoryName.includes('company basic')) {
      if (lowerFieldName.includes('name')) return true;
      if (lowerFieldName.includes('domain')) return true;
    }

    return false;
  };
  const isIntegrationFieldDisabled = (
    fieldKey: string,
    fieldName: string,
    categoryName?: string
  ): boolean => {
    const lowerFieldName = fieldName.toLowerCase();
    const lowerFieldKey = fieldKey.toLowerCase();
    const lowerCategoryName = categoryName?.toLowerCase() || '';

    if (lowerCategoryName.includes('people basic')) {
      if (lowerFieldName.includes('first name')) return true;
      if (lowerFieldName.includes('last name')) return true;
      if (lowerFieldName.includes('mobile phone')) return true;
      if (lowerFieldName.includes('blog/website')) return true;
      if (lowerFieldName.includes('home phone')) return true;
      if (lowerFieldName.includes('location')) return true;
      if (lowerFieldName.includes('linkedin')) return true;
      if (lowerFieldName.includes('email business')) return true;
    }

    if (lowerCategoryName.includes('company basic')) {
      if (lowerFieldName.includes('name')) return true;
      if (lowerFieldName.includes('domain')) return true;
    }

    return false;
  };

  /**
   * Determines if a field should be hidden from the export dialog.
   *
   * This function is used to conditionally hide specific fields that should not
   * be visible to users in the export options UI, even though they may exist
   * in the data structure.
   */
  const isFieldHidden = (
    fieldKey: string,
    fieldName: string,
    categoryName?: string
  ): boolean => {
    const lowerFieldKey = fieldKey.toLowerCase();
    const lowerCategoryName = categoryName?.toLowerCase() || '';

    // Hide 'company_url' field in Experience category
    // This field is hidden because it's redundant - the company URL information
    // is already available through other fields in the Experience category,
    // and showing it would create confusion or duplicate data in exports.
    if (lowerCategoryName.includes('experience')) {
      if (lowerFieldKey.includes('company_url')) return true;
    }
    return false;
  };

  // Helper function to determine if field should show "Coming Soon" badge
  const isFieldComingSoon = (
    fieldKey: string,
    fieldName: string,
    categoryName?: string
  ): boolean => {
    const lowerFieldName = fieldName.toLowerCase();
    const lowerCategoryName = categoryName?.toLowerCase() || '';

    if (lowerCategoryName.includes('people basic')) {
      if (lowerFieldName.includes('ai search input')) {
        return !isFeatureEnabled(
          FEATURE_FLAGS,
          FeatureFlags.EXPORT_PEOPLE_AI_SEARCH_INPUT
        );
      }
      if (lowerFieldName.includes('relevancy score')) {
        return !isFeatureEnabled(
          FEATURE_FLAGS,
          FeatureFlags.EXPORT_PEOPLE_RELEVANCY_SCORE
        );
      }
      if (lowerFieldName.includes('work direct phone')) {
        return !isFeatureEnabled(
          FEATURE_FLAGS,
          FeatureFlags.EXPORT_PEOPLE_WORK_DIRECT_PHONE
        );
      }
      if (lowerFieldName.includes('home phone')) {
        return !isFeatureEnabled(
          FEATURE_FLAGS,
          FeatureFlags.EXPORT_PEOPLE_HOME_PHONE
        );
      }
    }

    if (lowerCategoryName.includes('company basic')) {
      if (lowerFieldName.includes('follower count')) {
        return !isFeatureEnabled(
          FEATURE_FLAGS,
          FeatureFlags.EXPORT_COMPANY_FOLLOWER_COUNT
        );
      }
      if (lowerFieldName.includes('sic code')) {
        return !isFeatureEnabled(
          FEATURE_FLAGS,
          FeatureFlags.EXPORT_COMPANY_SIC_CODE
        );
      }
    }

    return false;
  };

  // Helper function to determine if field should have info badge
  const getFieldHasInfoBadge = (
    fieldKey: string,
    fieldName: string,
    categoryName?: string
  ): boolean => {
    const lowerFieldName = fieldName.toLowerCase();
    const lowerFieldKey = fieldKey.toLowerCase();
    const lowerCategoryName = categoryName?.toLowerCase() || '';

    if (lowerCategoryName.includes('people basic')) {
      if (lowerFieldName.includes('slug')) return true;
      if (lowerFieldName.includes('headline')) return true;
      if (lowerFieldName.includes('open to work')) return true;
      if (lowerFieldName.includes('premium')) return true;
      if (lowerFieldName.includes('influencer')) return true;
      if (lowerFieldName.includes('creator')) return true;
      if (lowerFieldName.includes('date of birth')) return true;
      if (lowerFieldName.includes('jobs count')) return true;
      if (lowerFieldName.includes('profile picture url')) return true;
      if (lowerFieldName.includes('summary')) return true;
    }

    return false; // Default return value
  };

  // Helper function to get field description based on field key, name, and category
  const getFieldDescription = (
    fieldKey: string,
    fieldName: string,
    categoryName?: string
  ): string => {
    const lowerFieldName = fieldName.toLowerCase();
    const lowerFieldKey = fieldKey.toLowerCase();
    const lowerCategoryName = categoryName?.toLowerCase() || '';

    // Category-specific descriptions and values
    if (lowerCategoryName.includes('people basic')) {
      if (lowerFieldName.includes('title'))
        return 'Senior Customer Engineer & Lead Integration Builder';
      if (lowerFieldName.includes('location'))
        return 'Tucson, Arizona, United States';
      if (lowerFieldName.includes('seniority')) return 'Manager';
      if (lowerFieldName.includes('department')) return 'Engineering';
      if (lowerFieldName.includes('first name')) return 'John';
      if (lowerFieldName.includes('last name')) return 'Doe';
      if (lowerFieldName.includes('full name')) return 'John Doe';
      if (lowerFieldName.includes('linkedin'))
        return 'https://linkedin.com/in/jdoe';
      if (lowerFieldName.includes('website') || lowerFieldName.includes('blog'))
        return 'https://jdoe.com';
      if (lowerFieldName.includes('mobile phone')) return '+(555) 123-4567';
      if (lowerFieldName.includes('work direct phone'))
        return '+(555) 123-4567';
      if (lowerFieldName.includes('home phone')) return '+(555) 123-4567';
      if (lowerFieldName.includes('headline'))
        return 'Customer focused full-stack engineer perpetually';
      if (lowerFieldName.includes('summary'))
        return 'I am a customer-focused software Engineer withally';
      if (lowerFieldName.includes('country')) return 'United States';
      if (lowerFieldName.includes('state')) return 'Arizona';
      if (lowerFieldName.includes('city')) return 'Tucson';
      if (lowerFieldName.includes('org')) return 'AI Pigeon';
      if (lowerFieldName.includes('slug')) return 'jdoe';
      if (lowerFieldName.includes('influencer')) return 'Yes';
      if (lowerFieldName.includes('premium')) return 'Yes';
      if (lowerFieldName.includes('open to work')) return 'No';
      if (lowerFieldName.includes('creator')) return 'No';
      if (lowerFieldName.includes('mx records')) return 'ASPMX.L.GOOGLE.COM';
      if (lowerFieldName.includes('email business'))
        return 'john.doe@company.com';
      if (lowerFieldName.includes('business status')) return 'Valid';
      if (lowerFieldName.includes('domain settings')) return 'smtp.google.com';
      if (lowerFieldName.includes('x') || lowerFieldName.includes('twitter'))
        return 'https://x.com/jdoe';
      if (lowerFieldName.includes('facebook'))
        return 'https://facebook.com/jdoe';
      if (lowerFieldName.includes('instagram'))
        return 'https://instagram.com/jdoe';
      if (lowerFieldName.includes('lists')) return 'Blacklist';
      if (lowerFieldName.includes('labels')) return 'Cold Leads';
      if (lowerFieldName.includes('owner')) return 'John Doe';
      if (lowerFieldName.includes('date of birth')) return '1993-12-02';
      if (lowerFieldName.includes('jobs count')) return '13';
      if (lowerFieldName.includes('profile picture url'))
        return 'https://linkedIn.com/in/jdoe';
      if (lowerFieldName.includes('ai ark contact id')) return '12312398123';
      if (lowerFieldName.includes('ai search input'))
        return 'https://www.linkedin.com/in/daniel-moreno-escamilla-2b248b159';
      if (lowerFieldName.includes('relevancy score')) return '89%';
      if (lowerFieldKey.includes('last_update')) return '15 days ago';
    }

    if (lowerCategoryName.includes('company basic')) {
      if (lowerFieldName.includes('name')) return 'Al Ark';
      if (lowerFieldName.includes('headcount')) return '30';
      if (lowerFieldName.includes('employee count')) return '30';
      if (lowerFieldName.includes('size')) return '11-50';
      if (lowerFieldName.includes('industry')) return 'Software Development';
      if (lowerFieldName.includes('product and services'))
        return 'psychology,pedagogical sciences,education';
      if (lowerFieldName.includes('description'))
        return 'Create live spreadsheets of people and...';
      if (lowerFieldName.includes('seo description'))
        return 'Create live spreadsheets of people and...';
      if (lowerFieldName.includes('domain url')) return 'ai-ark.com';
      if (lowerFieldName.includes('website')) return 'https://www.ai-ark.com';
      if (lowerFieldName.includes('linkedin'))
        return 'https://www.linkedin.com/company/ai-ark';
      if (lowerFieldName.includes('twitter') || lowerFieldName.includes('x'))
        return 'https://x.com/company.ai-ark';
      if (lowerFieldName.includes('facebook'))
        return 'https://www.facebook.com/company.ai-ark';
      if (lowerFieldName.includes('instagram'))
        return 'https://www.instagram.com/company.ai-ark';
      if (lowerFieldName.includes('slug')) return 'Al Ark';
      if (lowerFieldName.includes('type')) return 'Angel';
      if (lowerFieldName.includes('organization id')) return '60711322';
      if (lowerFieldName.includes('economic zone')) return 'GDP';
      if (lowerFieldName.includes('number of location')) return '3';
      if (lowerFieldName.includes('address'))
        return '456 Oak Street, Brooklyn, New York, United States';
      if (lowerFieldName.includes('street')) return '456 Oak Street';
      if (lowerFieldName.includes('location'))
        return 'Tucson, Arizona, United States';
      if (lowerFieldName.includes('country')) return 'United States';
      if (lowerFieldName.includes('state')) return 'New York';
      if (lowerFieldName.includes('city')) return 'Brooklyn';
      if (lowerFieldName.includes('postal code')) return '19234';
      if (lowerFieldName.includes('secondary')) return '+(555) 123-4567';
      if (lowerFieldName.includes('company phone')) return '+(555) 123-4567';
      if (lowerFieldName.includes('lists')) return 'Blacklist';
      if (lowerFieldName.includes('labels')) return 'Cold Leads';
      if (lowerFieldName.includes('owner')) return 'John Doe';
      if (lowerFieldName.includes('founding year')) return '2016';
      if (lowerFieldName.includes('annual revenue')) return '$3,000,000';
      if (lowerFieldName.includes('total funding')) return '$3,000,000';
      if (lowerFieldName.includes('last funding type')) return 'Angel';
      if (lowerFieldName.includes('last funding amount')) return '$3,000,000';
      if (lowerFieldName.includes('last funding date')) return '2023-02-27';
      if (lowerFieldName.includes('logo'))
        return 'https://s3.amazonaws.com/media.matrixrank';
      if (lowerFieldName.includes('follower count')) return '1699';
      if (lowerFieldName.includes('language')) return 'English';
      if (lowerFieldName.includes('technologies'))
        return 'psychology,pedagogical sciences,education';
      if (lowerFieldName.includes('ai ark account id')) return '2384102381';
      if (lowerFieldKey.includes('primary_email')) return 'info@amazon.com';
      if (lowerFieldKey.includes('last_update')) return '15 days ago';
      if (lowerFieldName.includes('naics code'))
        return '111110 | Soybean Farming';
    }

    if (lowerCategoryName.includes('company location')) {
      if (lowerFieldName.includes('is primary')) return 'No';
      if (lowerFieldName.includes('address'))
        return '456 Oak Street, Brooklyn, New York, United States';
    }

    if (lowerCategoryName.includes('experience')) {
      if (lowerFieldKey.includes('company_url')) return 'aboutamazon.com';
      if (lowerFieldName.includes('url'))
        return 'https://linkedIn.com/in/ai-ark';
      if (lowerFieldName.includes('title'))
        return 'Senior Customer Engineer & Lead Integration Builder';
      if (lowerFieldName.includes('org id')) return '20230227';
      if (lowerFieldName.includes('summary'))
        return 'I am a customer-focused software Engineer with';
      if (lowerFieldName.includes('company')) return 'AI Ark';
      if (lowerFieldName.includes('is current')) return 'Yes';
      if (lowerFieldName.includes('start date')) return '2023-02-27';
      if (lowerFieldName.includes('end date')) return '2023-02-27';
      if (lowerFieldName.includes('locality')) return 'Stony Brook New York';
    }

    if (lowerCategoryName.includes('education')) {
      if (lowerFieldName.includes('school name')) return 'Stony Brook New York';
      if (lowerFieldName.includes('degree')) return "Bachelor's Degree";
      if (lowerFieldKey.includes('field')) return 'Computer Science';
      if (lowerFieldName.includes('grade')) return '3.73';
      if (lowerFieldName.includes('start date')) return '2023-02-27';
      if (lowerFieldName.includes('end date')) return '2023-02-27';
    }

    if (lowerCategoryName.includes('award')) {
      if (lowerFieldName.includes('title')) return 'Essay Awards Winner';
      if (lowerFieldName.includes('date')) return '2023-02-27';
      if (lowerFieldName.includes('company name'))
        return 'The Japan Center at Stony Brook University';
      if (lowerFieldName.includes('summary'))
        return 'I won the sixth annual Japan Center at Stony';
    }

    if (lowerCategoryName.includes('course')) {
      if (lowerFieldName.includes('title')) return 'User Interface';
      if (lowerFieldName.includes('course number')) return '2121881239';
    }

    if (lowerCategoryName.includes('project')) {
      if (lowerFieldName.includes('title')) return 'Emote Control';
      if (lowerFieldName.includes('summary'))
        return 'Emote Control is an interactive multimedia application';
      if (lowerFieldName.includes('start date')) return '2023-02-27';
      if (lowerFieldName.includes('end date')) return '2023-02-27';
    }

    if (lowerCategoryName.includes('languages')) {
      if (lowerFieldName.includes('language')) return 'Japanese';
      if (lowerFieldName.includes('proficiency'))
        return 'Professional working proficiency';
    }

    if (lowerCategoryName.includes('certification')) {
      if (lowerFieldName.includes('title'))
        return 'Google Awards Video Certification';
      if (lowerFieldName.includes('date')) return '2023-02-27';
      if (lowerFieldName.includes('authority')) return 'ECE Fundamental';
      if (lowerFieldName.includes('license number')) return '103555';
      if (lowerFieldName.includes('display source')) return 'Elastic.co';
      if (lowerFieldName.includes('company id')) return '81760867';
      if (lowerFieldName.includes('verify url'))
        return 'https://training.elastic.co/award/confirmation';
      if (lowerFieldKey.includes('company_linkedin_url'))
        return 'https://www.linkedIn.com /company/elastic-co/';
      if (lowerFieldName.includes('company logo'))
        return 'https://media.licdn.com/dms/image';
    }

    if (lowerCategoryName.includes('volunteering')) {
      if (lowerFieldName.includes('summary'))
        return 'Emote Control is an interactive multimedia application';
      if (lowerFieldName.includes('role'))
        return 'Director of the efforts in the Tucson area';
      if (lowerFieldName.includes('company name')) return 'Red Cross';
      if (lowerFieldName.includes('company linkedIn url'))
        return '- https://www.linkedin.com/in/daniel-moreno-escamilla-2b248b159';
      if (lowerFieldName.includes('start date')) return '2023-02-27';
      if (lowerFieldName.includes('end date')) return '2023-02-27';
      if (lowerFieldKey.includes('company_linkedin_url'))
        return 'https://www.linkedIn.com /company/elastic-co/';
    }

    if (lowerCategoryName.includes('publications')) {
      if (lowerFieldName.includes('title'))
        return 'High Temperature Nanoprobing Application for';
      if (lowerFieldName.includes('summary'))
        return 'We test many issue on testing.org';
      if (lowerFieldName.includes('publisher'))
        return 'F. Rival, C.Q. Chen, P.T. Ng, PK.Tan, H.M and.';
      if (lowerFieldName.includes('date')) return '2023-02-27';
      if (lowerFieldName.includes('publication url'))
        return 'http://leeexplore.lee.org/document/43892473238957';
    }

    if (lowerCategoryName.includes('patent')) {
      if (lowerFieldName.includes('title')) return 'Emote Control';
      if (lowerFieldName.includes('issuer')) return 'US9897844';
      if (lowerFieldName.includes('summary'))
        return 'The Subject of th e Patent is around the ..';
      if (lowerFieldName.includes('patent number')) return '9897844';
      if (lowerFieldName.includes('application number')) return '9897844';
      if (lowerFieldName.includes('date')) return '2023-02-27';
    }

    return ''; // Default return value
  };

  // Sort categories by order
  const sortedCategories = Object.entries(optionsData).sort(
    ([, a], [, b]) => a.order - b.order
  );

  sortedCategories.forEach(([categoryKey, categoryData]) => {
    const categoryInfo = categoryMapping[categoryKey];

    if (!categoryInfo) {
      console.warn(`Unknown category key: ${categoryKey}`);
      return;
    }

    // Sort columns by order and convert to template fields
    const sortedColumns = Object.entries(categoryData.columns).sort(
      ([, a], [, b]) => a.order - b.order
    );

    const templateFields = sortedColumns.map(([fieldKey, fieldData]) => ({
      name: fieldData.label,
      icon: getIconForField(fieldKey, fieldData.label),
      description: getFieldDescription(
        fieldKey,
        fieldData.label,
        categoryInfo.name
      ),
      hasInfoBadge: getFieldHasInfoBadge(
        fieldKey,
        fieldData.label,
        categoryInfo.name
      ),
      selected: integrationConfig
        ? isIntegrationFieldSelectedByDefault(
            fieldKey,
            fieldData.label,
            categoryInfo.name
          )
        : isFieldSelectedByDefault(
            fieldKey,
            fieldData.label,
            categoryInfo.name
          ),
      disabled: integrationConfig
        ? isIntegrationFieldDisabled(
            fieldKey,
            fieldData.label,
            categoryInfo.name
          )
        : false,
      comingSoon: isFieldComingSoon(
        fieldKey,
        fieldData.label,
        categoryInfo.name
      ),
      hidden: isFieldHidden(fieldKey, fieldData.label, categoryInfo.name),
      type: fieldKey as OptionType,
    }));

    // Create the template category
    const templateCategory: TemplateCategory = {
      key: categoryInfo.key,
      name: categoryInfo.name,
      type: categoryInfo.type,
      ...(categoryInfo.hasInfoBadge && { hasInfoBadge: true }),
      ...(categoryInfo.isSingleEntry && { isSingleEntry: true }),
      ...(categoryInfo.disabled && { disabled: true }),
      ...(categoryInfo.hidden && { hidden: true }),

      entries: [
        {
          name: '1st Entry',
          checked: true,
          fields: templateFields,
        },
      ],
    };

    result.push(templateCategory);
  });

  return result;
};
